import { CalendarDays, Check, Loader2, MapPin, UserRound, UserRoundCheck } from 'lucide-react';
import { Button } from './ui/button';
import { accountLinkFormat, accountLinkSchedule } from '../utils/accountLinkDetails';

const Detail = ({ icon: Icon, label, id, children }) => (
  <div className="flex min-w-0 items-start gap-2.5">
    <Icon aria-hidden="true" className="mt-0.5 h-4 w-4 shrink-0 text-zinc-500" />
    <div className="min-w-0 [overflow-wrap:anywhere]">
      <dt data-testid={`${id}-label`} className="mb-1 text-xs text-zinc-400">{label}</dt>
      <dd className="text-sm leading-relaxed text-zinc-100">{children}</dd>
    </div>
  </div>
);

export const AccountLinkPrompt = ({ link, count, busy, error, onConfirm, onDismiss }) => {
  const schedule = accountLinkSchedule(link);
  return (
    <section data-testid="account-link-confirmation" aria-labelledby="account-link-title" aria-busy={busy}
      className="w-full border-b border-emerald-500/25 bg-zinc-900 px-4 py-5 sm:px-6">
      <div className="mx-auto max-w-6xl">
        <div className="mb-3 flex flex-wrap items-center gap-2.5">
          <UserRoundCheck aria-hidden="true" className="h-5 w-5 shrink-0 text-emerald-400" />
          <h2 id="account-link-title" data-testid="account-link-title" className="text-base font-semibold text-white sm:text-lg">Is this your player entry?</h2>
          {count > 1 && <span data-testid="account-link-pending-count" className="text-xs text-zinc-400">{count} pending</span>}
        </div>
        <div className="flex flex-col gap-4">
          <div className="min-w-0 flex-1">
            <div className="mb-4 flex flex-wrap items-center gap-x-3 gap-y-2">
              <p data-testid="account-link-event-name" className="min-w-0 text-lg font-semibold text-white [overflow-wrap:anywhere]">{link.event_name || 'Event name unavailable'}</p>
              <span data-testid="account-link-event-format" className="rounded-full border border-emerald-500/25 bg-emerald-500/10 px-2.5 py-0.5 text-xs font-medium text-emerald-300">{accountLinkFormat(link)}</span>
            </div>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
              <Detail icon={UserRound} label="Player entry" id="account-link-player-name">
                <span data-testid="account-link-player-name">{link.player_name || 'Player name unavailable'}</span>
              </Detail>
              <Detail icon={CalendarDays} label={link.date_kind === 'started' ? 'Event started' : 'Event date & time'} id="account-link-event-datetime">
                <span data-testid="account-link-event-date" className="block">{schedule.date}</span>
                <span data-testid="account-link-event-time" className="block text-xs text-zinc-400">{schedule.time}</span>
              </Detail>
              <Detail icon={MapPin} label="Course & location" id="account-link-event-location">
                <span data-testid="account-link-course-name" className="block">{link.course_name || 'Course unavailable'}</span>
                <span data-testid="account-link-event-location" className="block text-xs text-zinc-400">{link.location || 'Location unavailable'}</span>
              </Detail>
            </dl>
          </div>
          <div className="flex flex-wrap justify-end gap-2 border-t border-zinc-800 pt-4">
            <Button data-testid="account-link-confirm-button" disabled={busy} onClick={onConfirm}
              className="h-11 min-w-0 flex-1 gap-2 bg-emerald-600 px-4 text-white hover:bg-emerald-500 focus-visible:ring-emerald-400 sm:flex-none">
              {busy ? <Loader2 aria-hidden="true" className="h-4 w-4 animate-spin" /> : <Check aria-hidden="true" className="h-4 w-4" />}
              {busy ? 'Confirming…' : 'Yes, this is me'}
            </Button>
            <Button data-testid="account-link-dismiss-button" variant="ghost" disabled={busy} onClick={onDismiss}
              className="h-11 px-4 text-zinc-300 hover:bg-zinc-800 hover:text-white">Not now</Button>
          </div>
        </div>
        {error && <p data-testid="account-link-confirm-error" role="alert" className="mt-3 text-sm text-red-300">{error}</p>}
      </div>
    </section>
  );
};